import { Container, Point } from "pixi.js";
import type { PointData } from "pixi.js";
import type { PresentationTaskRunner, ViewportStore } from "@slot/presentation";
import type { CoinFountainTuning, FountainEmission } from "./CoinFountainTuning";
import { FountainEmitter } from "./FountainEmitter";
import type { FountainParticle } from "./FountainEmitter";

/** The fountain's tick runs in this scope of its runner; nothing slams it. */
const FOUNTAIN_SCOPE = "coin_fountain";

const ANCHOR_ORIGIN: PointData = { x: 0, y: 0 };
const ANCHOR_UNIT_X: PointData = { x: 1, y: 0 };

/** One coin's art, pooled by the fountain: shown and spinning while it flies, hidden and still in the pool. */
export interface FountainCoin {
  readonly node: Container;
  /** Spin at `rate` times the art's speed, from `phase` (0–1) of its loop. */
  startSpin(rate: number, phase: number): void;
  /** Stop spinning: nothing ticks it while it waits in the pool. */
  stopSpin(): void;
}

/**
 * A coin fountain: coins thrown up and out from behind `anchor`, falling under gravity out of the screen, each one
 * spinning on its own. It knows no game, so a later title can take it as it is: a title hands it its coin art
 * (`createCoin`), its tuning, the node to throw from and the runner to run on, then drives it with `run` (start, or
 * step up), `stopThrowing` (the coins in flight finish) and `clear` (all gone at once).
 *
 * Its parent is a full-screen layer with its origin at the viewport centre, in design units (a modal's backdrop):
 * coins fly over the whole design viewport and leave past its bottom or sides. The emitter point and the coins' size
 * follow `anchor` (its origin and scale seen from here, read every frame), so the fountain stays behind whatever the
 * layout made of that node on any shape. The motion is `FountainEmitter`'s; this view pools the coins, ticks the
 * emitter on the runner (paused and frame-stepped with it) and places the coins after each step. It ends its own tick
 * once nothing is thrown or in flight. It never takes a tap.
 */
export class CoinFountainView extends Container {
  private readonly coins: FountainCoin[] = [];
  private readonly emitter: FountainEmitter;
  private readonly anchor: Container;
  private readonly runner: PresentationTaskRunner;
  private readonly unsubscribeViewport: () => void;
  /** This frame's geometry for the emitter: the anchor read every step, the bounds on every viewport change. */
  private readonly area = { originX: 0, originY: 0, scale: 1, left: 0, top: 0, right: 0, bottom: 0 };
  private readonly anchorOrigin = new Point();
  private readonly anchorUnit = new Point();
  private ticking = false;
  /** Bumped when a tick ends: a tick of an earlier generation does nothing and stops at its next frame. */
  private tickGeneration = 0;

  public constructor(input: {
    /** One coin's art; called `tuning.maxCoins` times, up front. */
    createCoin: () => FountainCoin;
    tuning: CoinFountainTuning;
    /** The node the coins are thrown from behind: its origin is the emitter point, its scale sizes the coins. */
    anchor: Container;
    /** The design viewport the coins fly over. */
    viewport: ViewportStore;
    /** The clock the fountain runs on (an overlay's: the chrome runner). */
    runner: PresentationTaskRunner;
    /** Uniform in [0, 1); a test passes a seeded one. */
    random?: () => number;
  }) {
    super({ label: "CoinFountain" });
    this.eventMode = "none";
    // A larger coin is a nearer one: it draws in front.
    this.sortableChildren = true;
    this.anchor = input.anchor;
    this.runner = input.runner;
    for (let slot = 0; slot < input.tuning.maxCoins; slot++) {
      const coin = input.createCoin();
      coin.node.label = "FountainCoin";
      coin.node.visible = false;
      this.coins.push(coin);
      this.addChild(coin.node);
    }
    this.emitter = new FountainEmitter({
      tuning: input.tuning,
      random: input.random ?? Math.random,
      listener: { spawned: (particle) => this.show(particle), despawned: (particle) => this.hide(particle) },
    });
    this.unsubscribeViewport = input.viewport.subscribe((v) => {
      this.area.left = -v.design.width / 2;
      this.area.right = v.design.width / 2;
      this.area.top = -v.design.height / 2;
      this.area.bottom = v.design.height / 2;
    });
  }

  /** Coins in flight, or still to be thrown. */
  public get isRunning(): boolean {
    return !this.emitter.isIdle;
  }

  /** Run at `emission`: start throwing, or step up to it — its burst on the next frame, then its rate. */
  public run(emission: FountainEmission): void {
    this.emitter.run(emission);
    this.startTick();
  }

  /** Throw nothing more; the coins in flight finish their arcs, then the fountain goes quiet by itself. */
  public stopThrowing(): void {
    this.emitter.stopThrowing();
  }

  /** Every coin back in the pool at once, nothing more thrown. */
  public clear(): void {
    this.emitter.clear();
    this.endTick();
  }

  public dispose(): void {
    this.clear();
    this.unsubscribeViewport();
    this.destroy({ children: true });
  }

  private startTick(): void {
    if (this.ticking) return;
    this.ticking = true;
    const generation = this.tickGeneration;
    let last = 0;
    void this.runner.tickInScope(
      FOUNTAIN_SCOPE,
      (elapsed) => {
        if (generation !== this.tickGeneration) return;
        this.step(elapsed - last);
        last = elapsed;
        if (this.emitter.isIdle) this.endTick();
      },
      () => generation !== this.tickGeneration,
    );
  }

  private endTick(): void {
    if (!this.ticking) return;
    this.ticking = false;
    this.tickGeneration++;
  }

  private step(seconds: number): void {
    const origin = this.toLocal(ANCHOR_ORIGIN, this.anchor, this.anchorOrigin);
    const unit = this.toLocal(ANCHOR_UNIT_X, this.anchor, this.anchorUnit);
    this.area.originX = origin.x;
    this.area.originY = origin.y;
    this.area.scale = Math.hypot(unit.x - origin.x, unit.y - origin.y);
    this.emitter.update(seconds, this.area);
    for (const particle of this.emitter.particles) {
      const node = this.coins[particle.slot].node;
      node.position.set(particle.x, particle.y);
      node.rotation = particle.rotation;
      node.scale.set(particle.scale);
    }
  }

  private show(particle: FountainParticle): void {
    const coin = this.coins[particle.slot];
    coin.node.zIndex = particle.scale;
    coin.node.visible = true;
    coin.startSpin(particle.spinRate, particle.spinPhase);
  }

  private hide(particle: FountainParticle): void {
    const coin = this.coins[particle.slot];
    coin.node.visible = false;
    coin.stopSpin();
  }
}
